<template>
  <div class="select-location-btn-wrapper">
    <button
      class="btn btn-tertiary"
      @click="handleClick"
      id="OffersLocationFilterButton"
      :class="{
        'button-focus': isInputFocus
      }"
      data-testid="button-location-filter"
    >
      <button class="btn btn-inline btn-toggle-location" aria-label="Select location">
        <i
          class="icon-flag-map"
          :class="[
            {
              'icon-focus': isInputFocus
            },
            mainIconSize
          ]"
          data-testid="icon-location-filter"
        />
        <div class="location-heading-wrapper" @click="handleToggle(true)">
          <input
            v-if="!isDevice('tablet-l')"
            ref="inputLocation"
            @focusin="handleFocusIn"
            @focusout="handleFocusOut"
            v-model="location"
            type="text"
            class="input-location-filter"
            :placeholder="translate('offDealsLocationPlaceholderLarge')"
            data-testid="input-location-filter"
          />
          <span v-else class="text-mobile" data-testid="label-location-filter">{{ getMobileText }}</span>
        </div>
        <button
          id="clear-location-filter"
          v-show="areLocation && !isSeeAllFilterEurope"
          data-testid="clear-location-btn"
          class="btn btn-inline"
          aria-label="Reset location"
          @click="handleClearLocation"
        >
          <i class="icon-cross xs clear-dates-icon" />
        </button>
      </button>
    </button>
    <!-- Select dates dropdown -->
    <PortalTarget name="select-location-btn-wrapper" />
    <Portal :to="getPortalTarget()">
      <OffersLocationModal
        :location="location"
        :regions="regions.regions"
        :isOpen="isOpen"
        :countries="countries"
        :cities="cities"
        :is-full-screen="isDevice('tablet-l')"
        @toggle="handleToggle"
        @apply="handleApply"
        @clear="handleClearLocation"
        @inputLocation="handleInputLocation"
        :selected-region="selectedRegion"
        :current-filter-region="currentFilterRegion"
      />
    </Portal>
  </div>
</template>

<script>
import { media } from '@/infrastructure/Utils'
import OffersLocationModal from '@/components/offers/filters/OffersLocationModal'
import { createNamespacedHelpers } from 'vuex'
import { translate } from 'rhg-vue-common'

const { mapGetters, mapActions } = createNamespacedHelpers('newOffers')

export default {
  name: 'LocationFilter',
  components: { OffersLocationModal },
  props: {
    selectedRegion: {
      type: [String, Array],
      required: false,
      default: ''
    },
    mainIconSize: {
      type: String,
      required: false,
      default: 'sm'
    }
  },
  data() {
    return {
      location: '',
      isInputFocus: false,
      isOpen: false,
      activeFilter: ''
    }
  },
  computed: {
    ...mapGetters([
      'regions',
      'countries',
      'cities',
      'filter',
      'currentRegion',
      'currentCountry',
      'currentCity',
      'isSeeAllFilterEurope'
    ]),
    areInitialFilter() {
      return this.filter.nonEmpty()
    },
    currentFilterRegion() {
      return this.filter.region
    },
    currentFilterCountry() {
      if (this.filter.countries.length) {
        return this.filter.countries[0]
      }
      return ''
    },
    currentFilterCity() {
      if (this.filter.cities.length) {
        return this.filter.cities[0]
      }
      return ''
    },
    getMobileText() {
      return this.location && !this.isSeeAllFilterEurope ? this.location : translate('offDealsLocationPlaceholderShort')
    },
    areLocation() {
      return !!this.location
    },
    filterEmpty() {
      return this.filter.isEmpty()
    }
  },
  mounted() {
    this.handleCloseModal()
  },
  methods: {
    ...mapActions([
      'filterByRegion',
      'filterByGeoLocation',
      'filterByCountry',
      'filterByCity',
      'clearLocationFilters',
      'setIsAllFilterEurope'
    ]),
    setInitialLocationFilter() {
      if (this.filter.hasRegion()) {
        this.activeFilter = 'region'
      } else if (this.filter.hasCities()) {
        this.activeFilter = 'city'
      } else if (this.filter.hasCountries()) {
        this.activeFilter = 'country'
      } else if (this.filter.hasGeoLocation()) {
        this.activeFilter = 'geolocation'
      }
    },
    getLocationValue(value) {
      const filter = {
        region: this.currentRegion(this.currentFilterRegion)?.name,
        city: this.currentCity(this.currentFilterCity)?.name,
        country: this.currentCountry(this.currentFilterCountry)?.name,
        geolocation: translate('offDealsNearbyLocation')
      }
      return filter[value] || ''
    },
    handleInputLocation(value) {
      this.location = value
    },
    handleCloseModal() {
      window.addEventListener('click', e => {
        const buttonFilters = document.getElementById('OffersLocationFilterButton')
        const offersLocationModal = document.getElementById('offers-location-modal')
        if (offersLocationModal) {
          this.isOpen = offersLocationModal.contains(e.target)
        } else {
          this.isOpen = buttonFilters.contains(e.target)
        }
      })
    },
    async handleClearLocation() {
      await this.clearLocationFilters()
      this.activeFilter = ''
      this.$emit('filterLocation', '')
    },
    handleClick() {
      if (!this.isInputFocus && !this.isDevice('tablet-l')) {
        this.$refs.inputLocation.focus()
      }
    },
    handleToggle() {
      this.isOpen = false
    },
    handleFocusIn() {
      this.isInputFocus = true
    },
    handleFocusOut() {
      this.isInputFocus = false
    },
    async handleApply(optionsFilter) {
      this.isOpen = false
      const { region, city, country, activeFilter, position } = optionsFilter

      const filterBy = {
        region: async () => {
          await this.filterByRegion(region)
          this.$emit('filterLocation', region.name)
        },
        city: async () => {
          await this.filterByCity(city)
          this.$emit('filterLocation', city.name.split(',')[0])
        },
        country: async () => {
          await this.filterByCountry(country)
          this.$emit('filterLocation', country.name)
        },
        geolocation: async () => {
          await this.filterByGeoLocation(position)
        }
      }

      await filterBy[activeFilter]()

      this.activeFilter = activeFilter
    },
    isDevice(type) {
      const clientWidth = window.innerWidth
      return clientWidth <= media(type)
    },
    getPortalTarget() {
      const clientWidth = window.screen.availWidth
      return clientWidth > media('tablet-l') ? 'select-location-btn-wrapper' : 'modal-root-location'
    }
  },
  watch: {
    filterEmpty(value) {
      if (value === true) {
        this.location = ''
      }
    },
    activeFilter(newValue) {
      if (!this.isSeeAllFilterEurope) {
        this.location = this.getLocationValue(newValue)
        this.$emit('filterLocation', this.location)
      } else {
        this.setIsAllFilterEurope(false)
        this.activeFilter = ''
      }
    },
    location(value) {
      if (value === '') {
        this.activeFilter = ''
      }
      if (value === '' && this.$refs.inputLocation === document.activeElement) {
        this.isOpen = true
      }
    },
    areInitialFilter(newValue) {
      if (newValue === true && this.activeFilter === '') {
        this.setInitialLocationFilter()
      }
    }
  }
}
</script>

<style scoped lang="scss">
@media (max-width: media('tablet-l')) {
  .select-location-btn-wrapper {
    width: 100%;
  }
  .btn-toggle-location {
    height: 19px;
  }
}

.icon-focus {
  color: color('primary');
}
.button-focus {
  border-color: color('black');
}
.input-location-filter {
  border: none;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  caret-color: color('primary');
  width: 100%;
}

input:focus {
  outline: none;
}
</style>
